import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiClient, useAuth } from '../../auth-lib';
import OrgAdminLayout from '../components/OrgAdminLayout';
import ChatAssignmentModal from '../components/chat/ChatAssignmentModal';
import FileToClientModal from '../components/chat/FileToClientModal';
import { createChatSocket, emitAck } from '../../lib/chatSocket';
import ChatMessageBubble from '../../components/chat/ChatMessageBubble';
import { ACCEPT, checkFile, summarise } from '../../lib/chatFiles';
import useScrollBackLoader from '../../components/chat/useScrollBackLoader';
import TeamChat from '../../components/chat/TeamChat';
import { Button, Select, Skeleton, EmptyState, useToast } from '../../design-system';
import '../styles/Chat.css';

// The firm's side of the client conversation.
//
// The socket carries live traffic; REST carries history and is also the fallback
// path when the websocket is down, so the composer keeps working either way (see
// `send` below). Both produce identical rows server-side.

const FILTERS = [
  { value: 'all', label: 'All conversations' },
  { value: 'mine', label: 'Assigned to me' },
  { value: 'unread', label: 'Unread' },
];

const fmtDay = (d) => {
  if (!d) return '';
  const date = new Date(d);
  const today = new Date();
  const yest = new Date(); yest.setDate(today.getDate() - 1);
  const same = (a, b) => a.toDateString() === b.toDateString();
  if (same(date, today)) return 'Today';
  if (same(date, yest)) return 'Yesterday';
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
const fmtRel = (d) => {
  if (!d) return '';
  const mins = Math.round((Date.now() - new Date(d).getTime()) / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  if (mins < 1440) return `${Math.round(mins / 60)}h`;
  return `${Math.round(mins / 1440)}d`;
};

function OrgAdminChat() {
  const { user } = useAuth();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const activeClientId = params.get('client') || '';

  const [threads, setThreads] = useState([]);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const [messages, setMessages] = useState([]);
  const [thread, setThread] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const [canReassign, setCanReassign] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [connected, setConnected] = useState(false);
  const [peerTyping, setPeerTyping] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [uploading, setUploading] = useState(false);
  // The attachment whose "Attach to client records" dialog is open. Staff-only:
  // the portal renders the same bubble component without this handler, so the
  // long-press menu has nothing to show and never appears there.
  const [filingFile, setFilingFile] = useState(null);
  // Clients ↔ Team. Both are "chat", so they share one page rather than
  // splitting the concept across two nav entries.
  const [mode, setMode] = useState('clients');
  const [teamUnread, setTeamUnread] = useState(0);

  const socketRef = useRef(null);
  const fileRef = useRef(null);
  const bottomRef = useRef(null);
  // Older messages load as the reader scrolls up; the hook preserves their
  // reading position across the prepend.
  const { scrollRef, onScroll } = useScrollBackLoader({ hasMore, loadOlder: () => loadOlder() });
  const activeRef = useRef(activeClientId);
  activeRef.current = activeClientId;
  const typingTimer = useRef(null);

  // ── Socket lifecycle ──────────────────────────────────────────────────────
  useEffect(() => {
    const socket = createChatSocket('staff');
    socketRef.current = socket;

    socket.on('connect', () => setConnected(true));
    socket.on('disconnect', () => setConnected(false));
    socket.on('connect_error', () => setConnected(false));

    socket.on('chat:message', (msg) => {
      // Only append to the conversation actually on screen — every other
      // message is already reflected by the thread row's unread badge.
      if (String(msg.clientId) !== String(activeRef.current)) return;
      setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
    });

    socket.on('chat:thread', (row) => {
      setThreads((prev) => {
        const idx = prev.findIndex((t) => t.clientId === row.clientId);
        if (idx === -1) return [row, ...prev];
        const next = [...prev];
        // Keep the placeholder's client name if the wire row didn't carry one.
        next[idx] = { ...next[idx], ...row, clientName: row.clientName || next[idx].clientName };
        return next;
      });
      if (String(row.clientId) === String(activeRef.current)) setThread((t) => ({ ...t, ...row }));
    });

    socket.on('chat:typing', (evt) => {
      if (String(evt.clientId) !== String(activeRef.current)) return;
      setPeerTyping(evt.isTyping ? evt.who : null);
      if (evt.isTyping) {
        clearTimeout(typingTimer.current);
        typingTimer.current = setTimeout(() => setPeerTyping(null), 4000);
      }
    });

    return () => {
      clearTimeout(typingTimer.current);
      socket.removeAllListeners();
      socket.close();
      socketRef.current = null;
    };
  }, []);

  // ── Inbox ─────────────────────────────────────────────────────────────────
  const loadThreads = useCallback(() => {
    setLoadingThreads(true);
    apiClient
      .get('/api/chat/threads')
      .then((r) => setThreads(r.data.threads || []))
      .catch((e) => toast.danger(e.response?.data?.error || 'Failed to load conversations'))
      .finally(() => setLoadingThreads(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(loadThreads, [loadThreads]);

  // ── One conversation ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!activeClientId) { setThread(null); setMessages([]); return undefined; }
    setLoadingThread(true);
    setPeerTyping(null);
    setReplyTo(null);

    const socket = socketRef.current;
    if (socket) emitAck(socket, 'chat:open', { clientId: activeClientId });

    apiClient
      .get(`/api/chat/threads/${activeClientId}`)
      .then((r) => {
        setThread(r.data.thread);
        setMessages(r.data.messages || []);
        setHasMore(!!r.data.hasMore);
        setCanReassign(!!r.data.canReassign);
        // Opening a conversation is what marks it read, so clear the badge
        // locally too rather than waiting for the round trip to echo back.
        setThreads((prev) => prev.map((t) => (t.clientId === activeClientId ? { ...t, staffUnread: 0 } : t)));
        return apiClient.post(`/api/chat/threads/${activeClientId}/read`).catch(() => {});
      })
      .catch((e) => toast.danger(e.response?.data?.error || 'Failed to open conversation'))
      .finally(() => setLoadingThread(false));

    return () => {
      if (socket) socket.emit('chat:close', { clientId: activeClientId });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeClientId]);

  // Follow the conversation down only when something was APPENDED (a new
  // message arrived, or the thread changed) — never on a PREPEND from scrolling
  // up, which would yank the reader straight back to the bottom and make
  // loading history impossible.
  const lastIdRef = useRef(null);
  useEffect(() => {
    const lastId = messages[messages.length - 1]?.id || null;
    if (lastId !== lastIdRef.current) {
      bottomRef.current?.scrollIntoView({ block: 'end' });
      lastIdRef.current = lastId;
    }
  }, [messages, loadingThread]);

  const loadOlder = async () => {
    const oldest = messages[0];
    if (!oldest) return;
    try {
      const r = await apiClient.get(`/api/chat/threads/${activeClientId}/messages`, {
        params: { before: oldest.sentAt },
      });
      setMessages((prev) => [...(r.data.messages || []), ...prev]);
      setHasMore(!!r.data.hasMore);
    } catch (e) {
      toast.danger('Could not load older messages');
    }
  };

  const send = async (e) => {
    e?.preventDefault();
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    const socket = socketRef.current;
    try {
      // Socket first; fall back to REST when it isn't connected so a flaky
      // network degrades the experience rather than breaking the feature.
      const replyToId = replyTo?.id || null;
      const res = socket?.connected
        ? await emitAck(socket, 'chat:send', { clientId: activeClientId, body, replyToId })
        : { error: 'offline' };

      if (res.ok) {
        setDraft('');
        setReplyTo(null);
      } else {
        const r = await apiClient.post(`/api/chat/threads/${activeClientId}/messages`, { body, replyToId });
        setDraft('');
        setReplyTo(null);
        // Over REST there is no echo back to this tab from its own broadcast,
        // so append locally; the socket path already received it.
        setMessages((prev) => (prev.some((m) => m.id === r.data.message.id) ? prev : [...prev, r.data.message]));
      }
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Message not sent');
    } finally {
      setSending(false);
    }
  };

  // Upload and post in one request — the server writes the Document and the
  // message together, so a failed send never leaves an orphan file behind.
  const sendFile = async (file) => {
    const problem = checkFile(file);
    if (problem) { toast.danger(problem); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      if (draft.trim()) form.append('body', draft.trim());
      if (replyTo?.id) form.append('replyToId', replyTo.id);
      const r = await apiClient.post(`/api/chat/threads/${activeClientId}/attachments`, form);
      setDraft('');
      setReplyTo(null);
      // The socket broadcast reaches this tab too, but only if it is connected;
      // de-dupe by id so the message never appears twice.
      setMessages((prev) => (prev.some((m) => m.id === r.data.message.id) ? prev : [...prev, r.data.message]));
    } catch (err) {
      toast.danger(err.response?.data?.error || 'File could not be sent');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  // Presigned S3 links expire in five minutes, so they are minted on click
  // rather than up front for every file in the transcript.
  const openAttachment = async (file) => {
    try {
      const r = await apiClient.get(
        `/api/chat/threads/${activeClientId}/attachments/${file.documentId}/url`
      );
      return r.data.source === 'stream'
        ? `${apiClient.defaults.baseURL || ''}${r.data.url}`
        : r.data.url;
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Could not open that file');
      return null;
    }
  };

  const jumpToReply = (id) => {
    const el = document.getElementById(`chat-msg-${id}`);
    if (el) {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      el.classList.add('chat-msg-flash');
      setTimeout(() => el.classList.remove('chat-msg-flash'), 1200);
    }
  };

  const onDraft = (v) => {
    setDraft(v);
    const socket = socketRef.current;
    if (socket?.connected && activeClientId) {
      socket.emit('chat:typing', { clientId: activeClientId, isTyping: v.length > 0 });
    }
  };

  const visible = useMemo(() => {
    let list = threads;
    if (filter === 'mine') list = list.filter((t) => t.assigneeId === String(user?.userId));
    if (filter === 'unread') list = list.filter((t) => t.staffUnread > 0);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (t) => (t.clientName || '').toLowerCase().includes(q)
          || (t.clientCode || '').toLowerCase().includes(q)
          || (t.lastMessagePreview || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [threads, filter, search, user]);

  const totalUnread = threads.reduce((s, t) => s + (t.staffUnread || 0), 0);

  // Date separators: one per calendar day rather than per message.
  const rows = [];
  let lastDay = null;
  messages.forEach((m) => {
    const day = fmtDay(m.sentAt);
    if (day !== lastDay) { rows.push({ kind: 'day', id: `day-${day}-${m.id}`, day }); lastDay = day; }
    rows.push({ kind: 'msg', ...m });
  });

  return (
    <OrgAdminLayout
      title="Client Chat"
      subtitle={totalUnread > 0 ? `${totalUnread} unread message${totalUnread === 1 ? '' : 's'}` : 'Live conversations with your clients'}
      headerRight={
        mode === 'clients' ? (
          <span className={`chat-conn chat-conn-${connected ? 'on' : 'off'}`}>
            <span className="chat-conn-dot" />
            {connected ? 'Live' : 'Reconnecting…'}
          </span>
        ) : null
      }
    >
      <div className="chat-modes" role="tablist">
        <button
          type="button" role="tab" aria-selected={mode === 'clients'}
          className={`chat-mode${mode === 'clients' ? ' is-on' : ''}`}
          onClick={() => setMode('clients')}
        >
          🏢 Clients
          {totalUnread > 0 && <span className="chat-badge">{totalUnread}</span>}
        </button>
        <button
          type="button" role="tab" aria-selected={mode === 'team'}
          className={`chat-mode${mode === 'team' ? ' is-on' : ''}`}
          onClick={() => setMode('team')}
        >
          👥 Team
          {teamUnread > 0 && <span className="chat-badge">{teamUnread}</span>}
        </button>
      </div>

      {mode === 'team' && <TeamChat onUnreadChange={setTeamUnread} />}
      {mode === 'clients' && (
      <div className="chat-wrap">
        {/* ── Inbox ── */}
        <aside className="chat-list">
          <div className="chat-list-head">
            <input
              className="chat-search"
              placeholder="🔎 Search clients…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Select value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Filter conversations">
              {FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </Select>
          </div>

          <div className="chat-list-scroll">
            {loadingThreads ? (
              <div className="chat-list-loading"><Skeleton.Text lines={6} /></div>
            ) : visible.length === 0 ? (
              <p className="chat-empty-note">No conversations match.</p>
            ) : (
              visible.map((t) => (
                <button
                  type="button"
                  key={t.clientId}
                  className={`chat-row${t.clientId === activeClientId ? ' is-active' : ''}`}
                  onClick={() => setParams({ client: t.clientId })}
                >
                  <span className="chat-avatar" aria-hidden>{(t.clientName || '?').charAt(0).toUpperCase()}</span>
                  <span className="chat-row-main">
                    <span className="chat-row-top">
                      <span className="chat-row-name">{t.clientName}</span>
                      <span className="chat-row-time">{fmtRel(t.lastMessageAt)}</span>
                    </span>
                    <span className="chat-row-bottom">
                      <span className="chat-row-preview">
                        {t.lastMessageSenderType === 'staff' && <span className="chat-row-you">You: </span>}
                        {t.lastMessagePreview || <em>No messages yet</em>}
                      </span>
                      {t.staffUnread > 0 && <span className="chat-badge">{t.staffUnread}</span>}
                    </span>
                    {t.assigneeName && <span className="chat-row-owner">👤 {t.assigneeName}</span>}
                  </span>
                </button>
              ))
            )}
          </div>
        </aside>

        {/* ── Conversation ── */}
        <section className="chat-pane">
          {!activeClientId ? (
            <EmptyState icon="💬" title="Pick a conversation" description="Choose a client on the left to read and reply to their messages." />
          ) : (
            <>
              <header className="chat-pane-head">
                <div>
                  <h3 className="chat-pane-title">{thread?.clientName || '…'}</h3>
                  <p className="chat-pane-sub">
                    {thread?.assigneeName
                      ? <>Managed by <b>{thread.assigneeName}</b></>
                      : <span className="chat-unowned">Nobody is managing this chat</span>}
                    {thread?.clientCode ? ` · ${thread.clientCode}` : ''}
                  </p>
                </div>
                <Button variant="secondary" size="sm" onClick={() => setAssignOpen(true)}>
                  {canReassign ? 'Assign / history' : 'Assignment history'}
                </Button>
              </header>

              <div className="chat-scroll" ref={scrollRef} onScroll={onScroll}>
                {loadingThread ? (
                  <div className="chat-loading"><Skeleton.Text lines={8} /></div>
                ) : (
                  <>
                    {hasMore && (
                      <div className="chat-older">
                        <Button variant="secondary" size="sm" onClick={loadOlder}>Load older messages</Button>
                      </div>
                    )}
                    {rows.length === 0 && (
                      <p className="chat-empty-note chat-empty-center">
                        No messages yet — say hello and it will appear in the client's portal instantly.
                      </p>
                    )}
                    {rows.map((r) =>
                      r.kind === 'day' ? (
                        <div className="chat-day" key={r.id}><span>{r.day}</span></div>
                      ) : (
                        <div key={r.id} id={`chat-msg-${r.id}`}>
                          <ChatMessageBubble
                            message={r}
                            mine={r.senderType === 'staff'}
                            /* Name the sender only where it tells you
                               something: WHICH contact at the client wrote in
                               (one company has several portal logins on one
                               thread), and WHICH colleague replied when it
                               wasn't you. Your own name on your own message is
                               noise — which is what it was doing. */
                            showSender={
                              r.senderType === 'client'
                              || String(r.senderId) !== String(user?.userId)
                            }
                            onReply={setReplyTo}
                            onOpenAttachment={openAttachment}
                            onJumpToReply={jumpToReply}
                            onFileAttachment={setFilingFile}
                          />
                        </div>
                      )
                    )}
                    {peerTyping && <div className="chat-typing">{peerTyping} is typing…</div>}
                    <div ref={bottomRef} />
                  </>
                )}
              </div>

              {replyTo && (
                <div className="chat-reply-strip">
                  <div className="chat-reply-strip-main">
                    <span className="chat-reply-strip-who">Replying to {replyTo.senderName}</span>
                    <span className="chat-reply-strip-text">{summarise(replyTo)}</span>
                  </div>
                  <button type="button" className="chat-reply-cancel" onClick={() => setReplyTo(null)} aria-label="Cancel reply">✕</button>
                </div>
              )}

              <form className="chat-composer" onSubmit={send}>
                <input
                  ref={fileRef}
                  type="file"
                  accept={ACCEPT}
                  className="chat-file-input"
                  onChange={(e) => e.target.files?.[0] && sendFile(e.target.files[0])}
                />
                <button
                  type="button"
                  className="chat-attach-btn"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  title="Attach a file"
                  aria-label="Attach a file"
                >
                  {uploading ? '…' : '📎'}
                </button>
                <textarea
                  className="chat-input"
                  placeholder="Write a message…"
                  value={draft}
                  rows={1}
                  onChange={(e) => onDraft(e.target.value)}
                  onKeyDown={(e) => {
                    // Enter sends, Shift+Enter breaks the line — what everyone
                    // expects from a chat box.
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(e); }
                  }}
                />
                <Button type="submit" variant="primary" disabled={!draft.trim() || sending} loading={sending}>
                  Send
                </Button>
              </form>
            </>
          )}
        </section>
      </div>

      )}

      {filingFile && (
        <FileToClientModal
          clientId={activeClientId}
          clientName={thread?.clientName}
          file={filingFile}
          onClose={() => setFilingFile(null)}
        />
      )}

      {assignOpen && (
        <ChatAssignmentModal
          clientId={activeClientId}
          clientName={thread?.clientName}
          canReassign={canReassign}
          onClose={() => setAssignOpen(false)}
          onChanged={(row) => {
            setThread((t) => ({ ...t, ...row }));
            setThreads((prev) => prev.map((t) => (t.clientId === row.clientId ? { ...t, ...row } : t)));
          }}
        />
      )}
    </OrgAdminLayout>
  );
}

export default OrgAdminChat;
